import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ReviewItem,
  ReviewSummary,
  ReviewFilter,
  ReviewRating,
  ReviewHistoryItem,
} from '../types/review.types';
import type { PaginationMeta } from '../types/vocabulary.types';
import { reviewService } from '../services/review.service';
import { ReviewCard } from '../components/review/ReviewCard';
import { ReviewHistoryTable } from '../components/review/ReviewHistoryTable';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorMessage } from '../components/common/ErrorMessage';

const ReviewIcon = (): React.ReactElement => (
  <svg className="w-8 h-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const HistoryIcon = (): React.ReactElement => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export function ReviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Active view tab: 'session' | 'history'
  const activeTab = searchParams.get('tab') === 'history' ? 'history' : 'session';
  const setActiveTab = (tab: 'session' | 'history') => {
    setSearchParams(tab === 'history' ? { tab: 'history' } : {});
  };

  // Review Session state
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [summary, setSummary] = useState<ReviewSummary>({
    dueCount: 0,
    newCount: 0,
    reviewedCount: 0,
    totalCount: 0,
  });
  const [filter, setFilter] = useState<ReviewFilter>('due');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Session stats for completed screen
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [sessionRatings, setSessionRatings] = useState<{
    again: number;
    hard: number;
    good: number;
    easy: number;
  }>({
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
  });

  // Review History state
  const [historyItems, setHistoryItems] = useState<ReviewHistoryItem[]>([]);
  const [historyPagination, setHistoryPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Fetch review items
  const fetchSessionItems = useCallback(async (currentFilter: ReviewFilter) => {
    setIsLoadingSession(true);
    setSessionError(null);
    try {
      const res = await reviewService.getDueReviews(currentFilter, 100);
      setItems(res.items);
      setSummary(res.summary);
      setCurrentIndex(0);
      setIsRevealed(false);
      setSessionCompleted(false);
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : 'Failed to load review items.');
    } finally {
      setIsLoadingSession(false);
    }
  }, []);

  // Fetch history items
  const fetchHistory = useCallback(async (page: number) => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const res = await reviewService.getReviewHistory({ page, limit: 20 });
      setHistoryItems(res.items);
      setHistoryPagination(res.pagination);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Failed to load review history.');
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'session') {
      fetchSessionItems(filter);
    } else {
      fetchHistory(1);
    }
  }, [activeTab, filter, fetchSessionItems, fetchHistory]);

  const handleReveal = useCallback(() => {
    setIsRevealed(true);
  }, []);

  const handleRate = async (rating: ReviewRating) => {
    const currentItem = items[currentIndex];
    if (!currentItem || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await reviewService.submitReview(currentItem.id, rating);

      // Record session rating statistics
      setSessionRatings((prev) => ({
        ...prev,
        [rating.toLowerCase()]: prev[rating.toLowerCase() as keyof typeof prev] + 1,
      }));

      // Update summary counts
      setSummary((prev) => ({
        ...prev,
        dueCount: Math.max(0, prev.dueCount - 1),
        reviewedCount: prev.reviewedCount + 1,
        newCount:
          currentItem.learning.reviewCount === 0 ? Math.max(0, prev.newCount - 1) : prev.newCount,
      }));

      // Move to next card or complete session
      if (currentIndex < items.length - 1) {
        setCurrentIndex((prev) => prev + 1);
        setIsRevealed(false);
      } else {
        setSessionCompleted(true);
      }
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : 'Failed to submit review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keyboard shortcut listener for review session
  useEffect(() => {
    if (activeTab !== 'session' || sessionCompleted) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        if (!isRevealed) handleReveal();
      } else if (isRevealed && !isSubmitting) {
        if (e.key === '1') {
          e.preventDefault();
          handleRate('AGAIN');
        } else if (e.key === '2') {
          e.preventDefault();
          handleRate('HARD');
        } else if (e.key === '3') {
          e.preventDefault();
          handleRate('GOOD');
        } else if (e.key === '4') {
          e.preventDefault();
          handleRate('EASY');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, isRevealed, isSubmitting, sessionCompleted, handleReveal]);

  const currentItem = items[currentIndex];
  const totalInSession = items.length;
  const progressPercent =
    totalInSession > 0 ? Math.round((currentIndex / totalInSession) * 100) : 0;

  return (
    <div className="space-y-6 animate-slide-in pb-12">
      {/* ─── HEADER & NAVIGATION TABS ─────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Review & History</h1>
          <p className="text-sm text-slate-400 mt-1">
            Retain vocabulary permanently with active recall and spaced repetition scheduling.
          </p>
        </div>

        {/* View mode switcher */}
        <div className="inline-flex rounded-xl bg-surface-800/80 p-1 border border-white/10 self-start sm:self-auto">
          <button
            type="button"
            id="tab-session-btn"
            onClick={() => setActiveTab('session')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === 'session'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>Review Session</span>
            {summary.dueCount > 0 && (
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-rose-500 text-white">
                {summary.dueCount}
              </span>
            )}
          </button>
          <button
            type="button"
            id="tab-history-btn"
            onClick={() => setActiveTab('history')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === 'history'
                ? 'bg-primary-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <HistoryIcon />
            <span>Learning History</span>
          </button>
        </div>
      </div>

      {/* ─── TAB CONTENT: REVIEW SESSION ─────────────────────── */}
      {activeTab === 'session' && (
        <div className="space-y-6">
          {sessionError && (
            <ErrorMessage
              message={sessionError}
              onRetry={() => fetchSessionItems(filter)}
            />
          )}

          {/* Filter Bar & Hotkey Hints */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div id="review-filter-tabs" className="inline-flex rounded-xl bg-surface-800/80 p-1 border border-white/10">
              <button
                type="button"
                id="filter-due-btn"
                onClick={() => setFilter('due')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filter === 'due'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Due for Review ({summary.dueCount})
              </button>
              <button
                type="button"
                id="filter-new-btn"
                onClick={() => setFilter('new')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filter === 'new'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                New Words ({summary.newCount})
              </button>
              <button
                type="button"
                id="filter-reviewed-btn"
                onClick={() => setFilter('reviewed')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filter === 'reviewed'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Reviewed ({summary.reviewedCount})
              </button>
              <button
                type="button"
                id="filter-all-btn"
                onClick={() => setFilter('all')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filter === 'all'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                All Cards ({summary.totalCount})
              </button>
            </div>

            {/* Keyboard shortcuts guide */}
            <div className="hidden lg:flex items-center gap-2 text-xs text-slate-500 font-mono">
              <span><kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-white/10 text-slate-300">Space</kbd> Show</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">1</kbd> Again</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">2</kbd> Hard</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">3</kbd> Good</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">4</kbd> Easy</span>
            </div>
          </div>

          {/* Review Session Main Area */}
          {isLoadingSession ? (
            <div className="py-24 flex justify-center">
              <LoadingSpinner size="lg" label="Loading review cards..." />
            </div>
          ) : sessionCompleted ? (
            /* Session Completed Screen */
            <div
              id="session-completed-card"
              className="max-w-xl mx-auto rounded-3xl p-8 bg-surface-850 border border-white/10 text-center space-y-6 shadow-2xl"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Session Completed!</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Great work! You have finished reviewing all words in this deck.
                </p>
              </div>

              {/* Session Rating Summary Breakdown */}
              <div className="grid grid-cols-4 gap-2 pt-2">
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                  <span className="text-xs text-rose-300 font-semibold block">Again</span>
                  <span className="text-xl font-bold text-white">{sessionRatings.again}</span>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <span className="text-xs text-amber-300 font-semibold block">Hard</span>
                  <span className="text-xl font-bold text-white">{sessionRatings.hard}</span>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <span className="text-xs text-emerald-300 font-semibold block">Good</span>
                  <span className="text-xl font-bold text-white">{sessionRatings.good}</span>
                </div>
                <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20">
                  <span className="text-xs text-sky-300 font-semibold block">Easy</span>
                  <span className="text-xl font-bold text-white">{sessionRatings.easy}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-4 border-t border-white/10">
                <Button
                  id="restart-review-btn"
                  variant="primary"
                  onClick={() => fetchSessionItems(filter)}
                >
                  Review Again
                </Button>
                <Button
                  id="view-history-btn"
                  variant="secondary"
                  onClick={() => setActiveTab('history')}
                >
                  View Learning History
                </Button>
                <Button
                  id="goto-learn-btn"
                  variant="secondary"
                  onClick={() => navigate('/learn')}
                >
                  Go to Flashcards
                </Button>
              </div>
            </div>
          ) : items.length === 0 ? (
            /* Empty State */
            <EmptyState
              title={
                filter === 'due'
                  ? 'All Caught Up!'
                  : filter === 'new'
                  ? 'No New Unreviewed Words'
                  : 'No Cards Available'
              }
              description={
                filter === 'due'
                  ? "You have reviewed all cards currently due. Come back later or review your all/new cards anytime."
                  : summary.totalCount === 0
                  ? "You don't have any vocabulary words yet. Add some words to start reviewing!"
                  : "No vocabulary cards found under this filter."
              }
              icon={<ReviewIcon />}
              action={
                summary.totalCount === 0
                  ? {
                      label: 'Go to Vocabulary',
                      onClick: () => navigate('/vocabulary'),
                    }
                  : filter === 'due' && summary.totalCount > 0
                  ? {
                      label: 'Review All Words',
                      onClick: () => setFilter('all'),
                    }
                  : undefined
              }
            />
          ) : (
            /* Active Review Session */
            <div className="space-y-4">
              {/* Progress counter & bar */}
              <div className="max-w-2xl mx-auto flex items-center justify-between px-1">
                <span id="review-counter" className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Card {currentIndex + 1} of {items.length}
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {progressPercent}% completed
                </span>
              </div>
              <div className="max-w-2xl mx-auto w-full bg-surface-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-primary-500 h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              {/* Review Card Component */}
              {currentItem && (
                <ReviewCard
                  item={currentItem}
                  isRevealed={isRevealed}
                  onReveal={handleReveal}
                  onRate={handleRate}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── TAB CONTENT: LEARNING HISTORY ───────────────────── */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">Review Activity Timeline</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Complete log of your reviews and flashcard learning actions over time.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={isLoadingHistory}
              onClick={() => fetchHistory(historyPagination.page)}
            >
              Refresh
            </Button>
          </div>

          {historyError && (
            <ErrorMessage
              message={historyError}
              onRetry={() => fetchHistory(historyPagination.page)}
            />
          )}

          {isLoadingHistory ? (
            <div className="py-20 flex justify-center">
              <LoadingSpinner size="lg" label="Loading history..." />
            </div>
          ) : (
            <ReviewHistoryTable
              items={historyItems}
              pagination={historyPagination}
              isLoading={isLoadingHistory}
              onPageChange={(page) => fetchHistory(page)}
            />
          )}
        </div>
      )}
    </div>
  );
}
